/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */
import { apiSlice } from "../apiSlice";
import { API, API_SLICE_TAGS } from "../../../globals/globals";
import { FetchBaseQueryMeta } from "@reduxjs/toolkit/dist/query";
import {
  IAuditTrail,
  IProfile,
  IGeoRegion,
  ISmtpEmail,
  ISnmpAgent,
  ISnmpGroup,
  IUser,
  IVersion,
  IsnmpEmailGroups,
  IDevicesToGroup,
  ICustomEvent,
  ICustomEvents,
  IUserFileMgmt,
  ISyncOptions,
  ISshCliAccess,
  ISyslogForward,
  IKPIStorage,
  IRetentionData,
  IConfigureServer,
  IGetCongiguration,
  IDeviceSiteConfig,
  IThresholdCrossingSettingProfile,
  IThresholdCrossingSettingAction,
  IThresholdCrossingSettingSchedule,
  IThresholdCrossingSettingRule,
  IThresholdDevice,
  IThresholdDevicesResponse,
  IDevicesToProfile,
  IAccessControlCount,
} from "./types";
import { IArgs, IKeyValue, IPaginatedData, IRetentionPolicyResponse, IUserArgs, eventconfigIArgs, IThresholdCrossingArgs } from "../devices/types";
import { sanitizeQueryParams, encodeParam } from "../../helpers/helpers";
import { IEventConfig } from "../deviceConfig/types";

export const adminApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getSystemProperties: builder.query<IKeyValue, void>({
      query: () => ({
        url: `${API.ENDPOINTS.SYSTEMOPTIONS.path}`,
        method: "GET",
      }),
      providesTags: [API_SLICE_TAGS.SYSTEMOPTIONS],
    }),

    updateSystem: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.SYSTEMOPTIONS.path,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    getAccessControlSettings: builder.query<IKeyValue, void>({
      query: () => ({
        url: `${API.ENDPOINTS.ACCESSACONTROLSETTINGS.path}`,
        method: "GET",
      }),
      providesTags: [API_SLICE_TAGS.ACCESSCONTROLSETTINGS],
    }),

    updateAccessControlSettings: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.UPDATEACCESSCONTROLSETTINGS.path,
        method: "PATCH",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),

    createUser: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.CREATEUSER.path,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
      invalidatesTags: [API_SLICE_TAGS.USER],
    }),
    updateUser: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.UPDATEUSER.path,
        method: "PUT",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
      invalidatesTags: [API_SLICE_TAGS.USER],
    }),

    changePassword: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.CHANGEPASSWORD.path,
        method: "PUT",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),

    deleteUser: builder.mutation({
      query: (userId) => ({
        url: `${API.ENDPOINTS.DELETEUSER.path}/${userId}`,
        method: "DELETE",
      }),
      invalidatesTags: [API_SLICE_TAGS.USER],
    }),

    createProfile: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.PROFILE.path,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
      invalidatesTags: [API_SLICE_TAGS.PROFILES],
    }),

    updateProfile: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.PROFILE.path,
        method: "PUT",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
      invalidatesTags: [API_SLICE_TAGS.PROFILES],
    }),

    deleteProfile: builder.mutation({
      query: (args) => ({
        url: `${API.ENDPOINTS.PROFILE.path}/${args.profileId}${args.fallbackId && args.fallbackId.length > 0 && "?replacementId=" + args.fallbackId}`,
        method: "DELETE",
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
      invalidatesTags: [API_SLICE_TAGS.PROFILES],
    }),

    getUser: builder.query<IUser[], void>({
      query: () => ({ url: `${API.ENDPOINTS.USER.path}` }),
    }),

    getUserByName: builder.query<IUser, IUserArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.USER.path}/${args.name}` }),
    }),

    getUserBySelf: builder.query<IUser, IUserArgs>({
      query: () => ({ url: `${API.ENDPOINTS.USERSELF.path}` }),
    }),

    clearFailedLogin: builder.mutation<void, void>({
      query: () => ({
        url: `${API.ENDPOINTS.CLEARFAILEDLOGIN.path}`,
        method: "POST",
      }),
    }), 

    getProfile: builder.query<IProfile[], void>({
      query: () => ({ url: `${API.ENDPOINTS.PROFILE.path}s` }),
    }),

    getGeoRegions: builder.query<IGeoRegion[], void>({
      query: (args) => ({ url: `${API.ENDPOINTS.SITEGROUPS.path}` }),
      providesTags: [API_SLICE_TAGS.GEOREGIONS],
      transformResponse: (regions: IGeoRegion[]) =>
        [...regions]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((region) => ({
            ...region,
            siteGroups: region.siteGroups
              ? [...region.siteGroups].sort((a, b) => a.name.localeCompare(b.name))
              : region.siteGroups,
          })),
    }),

    getAuditTrail: builder.query<IPaginatedData<IAuditTrail>, IArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.AUDIT.path}?${sanitizeQueryParams(args.filters)}` }),
      providesTags: [API_SLICE_TAGS.AUDIT],
    }),

    getEventConfig: builder.query<IEventConfig[], eventconfigIArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.EVENTCONFIGURATION.path}/${args.logicalGroup}` }),
      providesTags: [API_SLICE_TAGS.EVENTCONFIGURATION],
    }),
    getEventConfigByGroup: builder.mutation({
      query: (args: any) => ({
        url: `${API.ENDPOINTS.EVENTCONFIGURATION.path}/${args.logicalGroup}`,
        method: "GET",
      }),
      // transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
      //   return { status: meta?.response?.status, ...data };
      // },
    }),
    updateEventConfig: builder.mutation({
      query: (payload) => ({
        url: `${API.ENDPOINTS.EVENTCONFIGURATION.path}`,
        method: "PUT",
        body: payload,
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),

    exportAuditLogs: builder.mutation({
      query: (args: IArgs) => ({
        url: `${API.ENDPOINTS.EXPORTAUDITLOGS.path}?${sanitizeQueryParams(args.filters)}`,
        method: "GET",
        responseHandler: "text",
      }),
    }),
    getNorthboundGroups: builder.query<ISnmpGroup[], IArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.NORTHBOUND.path}` }),
      providesTags: [API_SLICE_TAGS.NORTHBOUND],
    }),
    getSNMPGroup: builder.mutation({
      query: (args: any) => ({
        url: `${API.ENDPOINTS.SNMPGROUP.path}?groupName=${args?.group}`,
        method: "GET",
        responseHandler: "text",
      }),
    }),
    CreateSnmpGroup: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.CREATESNMPGROUP.path,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    updateSnmpGroup: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.SNMPGROUP.path,
        method: "PUT",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    deleteSnmpGroup: builder.mutation({
      query: (groupName) => ({
        url: `${API.ENDPOINTS.SNMPGROUP.path}/${groupName}`,
        method: "DELETE",
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    getSnmpAgent: builder.query<ISnmpAgent, void>({
      query: () => ({ url: `${API.ENDPOINTS.SNMPAGENT.path}` }),
      providesTags: [API_SLICE_TAGS.SNMPAGENT],
    }),
    updateSnmpAgent: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.SNMPAGENT.path,
        method: "PUT",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    getTrapRecieveSetting: builder.query<any, void>({
      query: () => ({ url: `${API.ENDPOINTS.TRAPRECIEVE.path}` }),
      providesTags: [API_SLICE_TAGS.TRAPRECIEVE],
    }),
    updateTrapRecieve: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.TRAPRECIEVE.path,
        method: "PUT",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    getEmailGroups: builder.query<any, void>({
      query: (args) => ({ url: `${API.ENDPOINTS.EMAILGROUPS.path}` }),
      providesTags: [API_SLICE_TAGS.EMAILGROUPS],
    }),
    getEmailGroupDetails: builder.mutation({
      query: (args: any) => ({
        url: `${API.ENDPOINTS.EMAILGROUP.path}?groupName=${args?.group}`,
        method: "GET",
        responseHandler: "text",
      }),
    }),
    CreateEmailGroup: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.EMAILGROUP.path,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    updateEmailGroup: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.EMAILGROUP.path,
        method: "PUT",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    deleteEmailGroup: builder.mutation({
      query: (groupName) => ({
        url: `${API.ENDPOINTS.EMAILGROUP.path}?groupName=${groupName}`,
        method: "DELETE",
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    getEmailSmtp: builder.query<ISmtpEmail, void>({
      query: () => ({ url: `${API.ENDPOINTS.SNMTPEMAIL.path}` }),
      providesTags: [API_SLICE_TAGS.SNMPAGENT],
    }),
    updateSmtpEmail: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.SNMTPEMAIL.path,
        method: "PUT",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    sendTestEmail: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.TESTEMAIL.path,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    CreateRegion: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.GEOREGIONS.path,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
      invalidatesTags: [API_SLICE_TAGS.REGIONS],
    }),
    updateRegion: builder.mutation({
      query: (payload) => ({
        url: `${API.ENDPOINTS.GEOREGIONS.path}/${payload.objectKey}`,
        method: "PUT",
        body: { name: payload.name, isDefault: payload.isDefault },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
      invalidatesTags: [API_SLICE_TAGS.REGIONS],
    }),
    deleteRegion: builder.mutation({
      query: (objectKey) => ({
        url: `${API.ENDPOINTS.GEOREGIONS.path}/${objectKey}`,
        method: "DELETE",
        responseHandler: (response: any) => response.text(),
      }),
      invalidatesTags: [API_SLICE_TAGS.REGIONS],
    }),
    CreateDomain: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.DOMAINS.path,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    updateDomain: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.DOMAINS.path,
        method: "PUT",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    deleteDomain: builder.mutation({
      query: (domainName) => ({
        url: `${API.ENDPOINTS.DOMAINS.path}/${domainName}`,
        method: "DELETE",
        responseHandler: (response: any) => response.text(),
      }),
    }),
    getVersion: builder.mutation<IVersion, void>({
      query: () => ({ url: `${API.ENDPOINTS.VERSION.path}`, method: "GET" }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    getSnmpEmailGroups: builder.query<IsnmpEmailGroups, void>({
      query: () => ({ url: `${API.ENDPOINTS.SNMPEMAILGROUPS.path}` }),
      providesTags: [API_SLICE_TAGS.SNMPAGENT],
    }),
    addDeviceToNotificationGroup: builder.mutation({
      query: (payload: IDevicesToGroup) => ({
        url: `${API.ENDPOINTS.GROUPS.path}/${payload.groupKey}/add`,
        method: "POST",
        body: payload.deviceKeys,
        //responseHandler: (response) => response.text(),
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { data: meta?.response, status: meta?.response?.status };
      },
    }),
    removeDevicesFromNotificationGroup: builder.mutation({
      query: (payload: IDevicesToGroup) => ({
        url: `${API.ENDPOINTS.GROUPS.path}/${payload.groupKey}/remove`,
        method: "POST",
        body: payload.deviceKeys,
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    createCustomEvent: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.CUSTOMEVENT.path,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    getCustomEvents: builder.query<ICustomEvents, void>({
      query: () => ({ url: `${API.ENDPOINTS.CUSTOMEVENTS.path}` }),
      providesTags: [API_SLICE_TAGS.CUSTOMEVENTS],
    }),
    getCustomEvent: builder.query<ICustomEvent, void>({
      query: (eventName) => ({
        url: `${API.ENDPOINTS.CUSTOMEVENT.path}/${eventName}`,
        providesTags: [API_SLICE_TAGS.CUSTOMEVENT],
      }),
    }),
    updateCustomEvent: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.CUSTOMEVENT.path,
        method: "PUT",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    deleteCustomEvent: builder.mutation({
      query: (eventName) => ({
        url: `${API.ENDPOINTS.CUSTOMEVENT.path}/${eventName}`,
        method: "DELETE",
        responseHandler: (response) => response.text(),
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { data: meta?.response, status: meta?.response?.status };
      },
    }),
    getUsersFileMgmt: builder.query<IUserFileMgmt[], void>({
      query: (eventName) => ({
        url: `${API.ENDPOINTS.USERSFILEMGMT.path}`,
        providesTags: [API_SLICE_TAGS.USERSFILEMGMT],
      }),
    }),

    //EXTERNAL USERS MANAGEMENT QUERY

    getExternalUsersFileMgmt: builder.query<IUserFileMgmt[], void>({
      query: (eventName) => ({
        url: `${API.ENDPOINTS.EXTERNALUSERSFILEMGMT.path}`,
        providesTags: [API_SLICE_TAGS.USERSFILEMGMT],
      }),
    }),

    createUsersFileMgmt: builder.mutation({
      query: (payload: IUserFileMgmt) => ({
        url: API.ENDPOINTS.USERSFILEMGMT.path,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    updateUsersFileMgmt: builder.mutation({
      query: (payload: IUserFileMgmt) => ({
        url: `${API.ENDPOINTS.USERSFILEMGMT.path}/${payload.id}`,
        method: "PUT",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    deleteUsersFileMgmt: builder.mutation({
      query: (payload: IUserFileMgmt) => ({
        url: `${API.ENDPOINTS.USERSFILEMGMT.path}/${payload.username}`,
        method: "DELETE",
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),

    // EXTERNAL USER CREATE UPDATE DELETE 

    createExternalUsersFileMgmt: builder.mutation({
      query: (payload: IUserFileMgmt) => ({
        url: API.ENDPOINTS.EXTERNALUSERSFILEMGMT.path,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    updateExternalUsersFileMgmt: builder.mutation({
      query: (payload: IUserFileMgmt) => ({
        url: `${API.ENDPOINTS.EXTERNALUSERSFILEMGMT.path}/${payload.id}`,
        method: "PUT",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    deleteExternalUsersFileMgmt: builder.mutation({
      query: (payload: IUserFileMgmt) => ({
        url: `${API.ENDPOINTS.EXTERNALUSERSFILEMGMT.path}/${payload.username}`,
        method: "DELETE",
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),

    getSyncOption: builder.query<ISyncOptions, void>({
      query: () => ({
        url: `${API.ENDPOINTS.SYNCOPTIONS.path}`,
        providesTags: [API_SLICE_TAGS.SYNCOPTIONS],
      }),
    }),
    updateSyncOption: builder.mutation({
      query: (payload: ISyncOptions) => ({
        url: API.ENDPOINTS.SYNCOPTIONS.path,
        method: "PUT",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    getSshCliAccess: builder.query<ISshCliAccess, void>({
      query: () => ({
        url: `${API.ENDPOINTS.SSHCLIACCESS.path}`,
        providesTags: [API_SLICE_TAGS.SSHCLIACCESS],
      }),
    }),
    updateSshCliAccess: builder.mutation({
      query: (payload: ISshCliAccess) => ({
        url: API.ENDPOINTS.SSHCLIACCESS.path,
        method: "PUT",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    getSyslogs: builder.query<ISyslogForward[], void>({
      query: () => ({ url: `${API.ENDPOINTS.SYSLOGS.path}s` }),
      providesTags: [API_SLICE_TAGS.SYSLOGS],
    }),

    createSyslog: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.SYSLOGS.path,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    updateSyslog: builder.mutation({
      query: (args) => ({
        url: `${API.ENDPOINTS.SYSLOGS.path}?id=${args.objectKey}`,
        method: "PUT",
        body: args.payload,
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    deleteSyslog: builder.mutation({
      query: (objectKey) => ({
        url: `${API.ENDPOINTS.SYSLOGS.path}?id=${objectKey}`,
        method: "DELETE",
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { data: meta?.response, status: meta?.response?.status };
      },
    }),
    getKPIStorage: builder.query<IKPIStorage, null>({
      query: () => ({
        url: `${API.ENDPOINTS.KPISTORAGE.path}`,
        providesTags: [API_SLICE_TAGS.KPISTORAGE],
      }),
    }),

    getPVCStorage: builder.query<IKPIStorage, null>({
      query: () => ({
        url: `${API.ENDPOINTS.PVCSTORAGE.path}`,
        providesTags: [API_SLICE_TAGS.PVCSTORAGE],
      }),
    }),

    getDBStorage: builder.query<IKPIStorage, null>({
      query: () => ({
        url: `${API.ENDPOINTS.DBSTORAGE.path}`,
        providesTags: [API_SLICE_TAGS.DBSTORAGE],
      }),
    }),
    getPMFileStorage: builder.query<IKPIStorage, null>({
      query: () => ({
        url: `${API.ENDPOINTS.PMFILESTORAGE.path}`,
        providesTags: [API_SLICE_TAGS.PMFILESTORAGE],
      }),
    }),

    // GET RETENTION POLICIES API 
    getRetentionPolicies: builder.query<IRetentionPolicyResponse, void>({
      query: () => ({
        url: `${API.ENDPOINTS.RETENTIONPOLICIES.path}`,
        method: "GET",
      }),
    }),

    // GEO REDUNDANCY APIs 

    configurePrimary: builder.mutation<void, IConfigureServer>({
      query: (config) => ({
        url: `${API.ENDPOINTS.CONFIGUREPRIMARY.path}`,
        method: "POST",
        body: config,
      }),
    }),


    // getStatus : builder.query<IGetCongiguration,void>({
    //   query: () => ({
    //     url: API.ENDPOINTS.STATUS.path,
    //     method: "GET"
    //   }),
    //   transformResponse: (response: IGetCongiguration) => response as IGetCongiguration,
    // }),

    getConfiguration: builder.query<IGetCongiguration, void>({
      query: () => ({
        url: API.ENDPOINTS.GETCONFIGURATION.path,
        method: "GET",
      }),
      transformResponse: (response: IGetCongiguration) => response as IGetCongiguration,
    }),

    resetActive: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.RESETACTIVE.path,
        method: "PUT",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),

    failover: builder.mutation({
      query: (payload) => ({
        url: `${API.ENDPOINTS.FAILOVER.path}`,
        method: "PUT",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),

    getStatus: builder.query<IGetCongiguration, void>({
      query: () => ({
        url: API.ENDPOINTS.STATUS.path,
        method: "GET"
      }),
      transformResponse: (response: IGetCongiguration) => response as IGetCongiguration,
    }),

    testConfiguration: builder.mutation<void, IConfigureServer>({
      query: (config) => ({
        url: `${API.ENDPOINTS.TESTCONFIGURATION.path}`,
        method: "POST",
        body: config,
      }),
    }),
    importOfflineSite: builder.mutation<any, { formData: FormData }>({
      query: ({ formData }) => ({
        url: `${API.ENDPOINTS.ADDOFFSITE.path}`,
        method: "POST",
        body: formData
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    addDeviceSite: builder.mutation<void, IDeviceSiteConfig>({
      query: (config) => ({
        url: `${API.ENDPOINTS.ADDDEVICE.path}`,
        method: "POST",
        body: config,
      }),
    }),
    updateDasUiPort: builder.mutation<void, { objectKey: string; urlAddress: string }>({
      query: ({ objectKey, urlAddress }) => ({
        url: `v1/device_config/offlinedevice/${objectKey}/ui-port`,
        method: "PUT",
        body: { urlAddress },
      }),
    }),
    getDataPollers: builder.query<any, void>({
      query: () => ({ url: `${API.ENDPOINTS.DATAPOLLING.path}` }),
      //providesTags: [API_SLICE_TAGS.SNMPAGENT],
    }),
    updateDataPollers: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.DATAPOLLING.path,
        method: "PUT",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    getPasswordSettings: builder.query<boolean, void>({
      query: () => ({
        url: API.ENDPOINTS.PASSWORDSETTINGS.path,
        method: "GET",
      }),
      transformResponse: (response: boolean) => {
        return response;
      },
    }),
    getThresholdCrossingSettingProfileById: builder.query<IThresholdCrossingSettingProfile, IThresholdCrossingArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.THRESHOLDCROSSINGSETTINGPROFILE.path}/${args.id}` }),
    }),
    getThresholdCrossingSettingProfile: builder.query<IThresholdCrossingSettingProfile[], void>({
      query: () => ({ url: `${API.ENDPOINTS.THRESHOLDCROSSINGSETTINGPROFILE.path}` }),
    }),
    getThresholdCrossingSettingAction: builder.query<IThresholdCrossingSettingAction[], IThresholdCrossingArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.THRESHOLDCROSSINGSETTINGACTION.path}?profileId=${args.id}` }),
    }),
    getThresholdCrossingSettingSchedule: builder.query<IThresholdCrossingSettingSchedule[], IThresholdCrossingArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.THRESHOLDCROSSINGSETTINGSCHEDULE.path}?profileId=${args.id}` }),
    }),
    getThresholdCrossingSettingRule: builder.query<IThresholdCrossingSettingRule[], IThresholdCrossingArgs>({
      query: (args) => ({ url: `${API.ENDPOINTS.THRESHOLDCROSSINGSETTINGRULE.path}?profileId=${args.id}` }),
    }),
    CreateThresholdCrossingSettingProfile: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.THRESHOLDCROSSINGSETTINGPROFILE.path,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    updateThresholdCrossingSettingProfile: builder.mutation({
      query: (payload) => ({
        url: `${API.ENDPOINTS.THRESHOLDCROSSINGSETTINGPROFILE.path}/${payload.id}`,
        method: "PUT",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    deleteThresholdCrossingSettingProfile: builder.mutation({
      query: (id) => ({
        url: `${API.ENDPOINTS.THRESHOLDCROSSINGSETTINGPROFILE.path}/${id}`,
        method: "DELETE",
        responseHandler: (response: any) => response.text(),
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    CreateThresholdCrossingSettingAction: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.THRESHOLDCROSSINGSETTINGACTION.path,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    updateThresholdCrossingSettingAction: builder.mutation({
      query: (payload) => ({
        url: `${API.ENDPOINTS.THRESHOLDCROSSINGSETTINGACTION.path}/${payload.id}`,
        method: "PUT",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    deleteThresholdCrossingSettingAction: builder.mutation({
      query: (id) => ({
        url: `${API.ENDPOINTS.THRESHOLDCROSSINGSETTINGACTION.path}/${id}`,
        method: "DELETE",
        responseHandler: (response: any) => response.text(),
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),

    CreateThresholdCrossingSettingSchedule: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.THRESHOLDCROSSINGSETTINGSCHEDULE.path,
        method: "POST",
        body: { ...payload },
        responseHandler: (response: any) => response.text()
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    updateThresholdCrossingSettingSchedule: builder.mutation({
      query: (payload) => ({
        url: `${API.ENDPOINTS.THRESHOLDCROSSINGSETTINGSCHEDULE.path}/${payload.id}`,
        method: "PUT",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    deleteThresholdCrossingSettingSchedule: builder.mutation({
      query: (id) => ({
        url: `${API.ENDPOINTS.THRESHOLDCROSSINGSETTINGSCHEDULE.path}/${id}`,
        method: "DELETE",
        responseHandler: (response: any) => response.text(),
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    CreateThresholdCrossingSettingRule: builder.mutation({
      query: (payload) => ({
        url: API.ENDPOINTS.THRESHOLDCROSSINGSETTINGRULE.path,
        method: "POST",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    updateThresholdCrossingSettingRule: builder.mutation({
      query: (payload) => ({
        url: `${API.ENDPOINTS.THRESHOLDCROSSINGSETTINGRULE.path}/${payload.id}`,
        method: "PUT",
        body: { ...payload },
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    deleteThresholdCrossingSettingRule: builder.mutation({
      query: (id) => ({
        url: `${API.ENDPOINTS.THRESHOLDCROSSINGSETTINGRULE.path}/${id}`,
        method: "DELETE",
        responseHandler: (response: any) => response.text(),
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { status: meta?.response?.status, ...data };
      },
    }),
    getNotificationGroups: builder.query<any, void>({
      query: (args) => ({ url: `${API.ENDPOINTS.NOTIFICATIONGROUPS.path}` }),
      providesTags: [API_SLICE_TAGS.NOTIFICATIONGROUPS],
    }),

    //GET/ADD/DELETE DEVICES IN ADMIN PERFORMANCE THRESHOLD DEVICE

    getThresholdMappedDevices: builder.query<IThresholdDevicesResponse, IThresholdCrossingArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.THRESHOLDDEVICES.path}/${args.id}/devices?type=mapped&${sanitizeQueryParams(args.filters)}`,
        method: "GET",
      }),
      transformResponse: (response: IThresholdDevicesResponse) => response,
    }),

    getThresholdUnmappedDevices: builder.query<IThresholdDevicesResponse, IThresholdCrossingArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.THRESHOLDDEVICES.path}/${args.id}/devices?type=unmapped&${sanitizeQueryParams(args.filters)}`,
        method: "GET",
      }),
      transformResponse: (response: IThresholdDevicesResponse) => response,
    }),
    getThresholdAllUnmappedDevices: builder.query<any[], IThresholdCrossingArgs>({
      query: (args) => ({
        url: `${API.ENDPOINTS.THRESHOLDDEVICES.path}/${args.id}/devices/unmapped?${sanitizeQueryParams(args.filters)}`,
        method: "GET",
      }),
      transformResponse: (response: any[]) => response,
    }),
    addDeviceToProfile: builder.mutation({
      query: (payload: IDevicesToProfile) => ({
        url: `${API.ENDPOINTS.THRESHOLDDEVICES.path}/${payload.profileKey}/devices`,
        method: "POST",
        body: payload.deviceKeys,
        //responseHandler: (response) => response.text(),
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { data: meta?.response, status: meta?.response?.status };
      },
    }),

    removeDeviceFromProfile: builder.mutation({
      query: (payload: IDevicesToGroup) => ({
        url: `${API.ENDPOINTS.THRESHOLDDEVICES.path}/${payload.id}/devices`,
        method: "DELETE",
        body: payload.deviceKeys,
        //responseHandler: (response) => response.text(),
      }),
      transformResponse: (data: any, meta: FetchBaseQueryMeta) => {
        return { data: meta?.response, status: meta?.response?.status };
      },
    }),

    getAccessControlCount: builder.query<IAccessControlCount, void>({
      query: () => ({
        url: `${API.ENDPOINTS.ADMIN.path}/access-control-count`,
        method: "GET",
      }),
      transformResponse: (response: any) => {
        if (response?.data) { return response.data as IAccessControlCount;}
        return response as IAccessControlCount;
      },
      providesTags: [API_SLICE_TAGS.USER, API_SLICE_TAGS.PROFILES, API_SLICE_TAGS.REGIONS],
    }),

  }),
});

export const {
  // useGetSystemPropertiesMutation,
  useGetSystemPropertiesQuery,
  useUpdateSystemMutation,
  useGetAccessControlSettingsQuery,
  useUpdateAccessControlSettingsMutation,
  useCreateUserMutation,
  useUpdateUserMutation,
  useChangePasswordMutation,
  useDeleteUserMutation,
  useCreateProfileMutation,
  useUpdateProfileMutation,
  useDeleteProfileMutation,
  useGetUserQuery,
  useGetUserByNameQuery,
  useGetUserBySelfQuery,
  useGetProfileQuery,
  // useGetDomainsQuery,
  useGetGeoRegionsQuery,
  useGetAuditTrailQuery,
  useGetEventConfigQuery,
  useGetEventConfigByGroupMutation,
  useExportAuditLogsMutation,
  useUpdateEventConfigMutation,
  useGetNorthboundGroupsQuery,
  useGetSNMPGroupMutation,
  useCreateSnmpGroupMutation,
  useUpdateSnmpGroupMutation,
  useDeleteSnmpGroupMutation,
  useGetSnmpAgentQuery,
  useUpdateSnmpAgentMutation,
  useGetTrapRecieveSettingQuery,
  useUpdateTrapRecieveMutation,
  useGetEmailGroupsQuery,
  useGetEmailGroupDetailsMutation,
  useCreateEmailGroupMutation,
  useUpdateEmailGroupMutation,
  useDeleteEmailGroupMutation,
  useGetEmailSmtpQuery,
  useUpdateSmtpEmailMutation,
  useCreateRegionMutation,
  useUpdateRegionMutation,
  useDeleteRegionMutation,
  useCreateDomainMutation,
  useUpdateDomainMutation,
  useDeleteDomainMutation,
  useGetVersionMutation,
  useGetSnmpEmailGroupsQuery,
  useAddDeviceToNotificationGroupMutation,
  useRemoveDevicesFromNotificationGroupMutation,
  useCreateCustomEventMutation,
  useGetCustomEventsQuery,
  useGetCustomEventQuery,
  useUpdateCustomEventMutation,
  useDeleteCustomEventMutation,
  useGetUsersFileMgmtQuery,
  useGetExternalUsersFileMgmtQuery,
  useCreateUsersFileMgmtMutation,
  useUpdateUsersFileMgmtMutation,
  useDeleteUsersFileMgmtMutation,
  useCreateExternalUsersFileMgmtMutation,
  useUpdateExternalUsersFileMgmtMutation,
  useDeleteExternalUsersFileMgmtMutation,
  useGetSyncOptionQuery,
  useUpdateSyncOptionMutation,
  useGetSshCliAccessQuery,
  useUpdateSshCliAccessMutation,
  useGetSyslogsQuery,
  useCreateSyslogMutation,
  useUpdateSyslogMutation,
  useDeleteSyslogMutation,
  useGetKPIStorageQuery,
  useGetPVCStorageQuery,
  useGetPMFileStorageQuery,
  useGetDBStorageQuery,
  useGetRetentionPoliciesQuery,
  useSendTestEmailMutation,
  useConfigurePrimaryMutation,
  useGetConfigurationQuery,
  useResetActiveMutation,
  useFailoverMutation,
  useGetStatusQuery,
  useTestConfigurationMutation,
  useImportOfflineSiteMutation,
  useAddDeviceSiteMutation,
  useUpdateDasUiPortMutation,
  useGetDataPollersQuery,
  useUpdateDataPollersMutation,
  useGetPasswordSettingsQuery,
  useGetThresholdCrossingSettingProfileByIdQuery,
  useGetThresholdCrossingSettingProfileQuery,
  useGetThresholdCrossingSettingActionQuery,
  useGetThresholdCrossingSettingRuleQuery,
  useGetThresholdCrossingSettingScheduleQuery,
  useCreateThresholdCrossingSettingProfileMutation,
  useUpdateThresholdCrossingSettingProfileMutation,
  useDeleteThresholdCrossingSettingProfileMutation,
  useCreateThresholdCrossingSettingActionMutation,
  useUpdateThresholdCrossingSettingActionMutation,
  useDeleteThresholdCrossingSettingActionMutation,
  useCreateThresholdCrossingSettingScheduleMutation,
  useUpdateThresholdCrossingSettingScheduleMutation,
  useDeleteThresholdCrossingSettingScheduleMutation,
  useCreateThresholdCrossingSettingRuleMutation,
  useUpdateThresholdCrossingSettingRuleMutation,
  useDeleteThresholdCrossingSettingRuleMutation,
  useGetNotificationGroupsQuery,
  useGetThresholdMappedDevicesQuery,
  useGetThresholdUnmappedDevicesQuery,
  useGetThresholdAllUnmappedDevicesQuery,
  useAddDeviceToProfileMutation,
  useRemoveDeviceFromProfileMutation,
  useClearFailedLoginMutation,
  useGetAccessControlCountQuery,
} = adminApiSlice;
